import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PhoneInput from 'react-phone-input-2';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  CustomInput, CustomPhoneNumber, defaultValidateMessages, DiscardModal,
  EIconName,
  InputReadOnly, stringPattern
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './general-data.scss';

// Store entities
import { updateProfile } from '../../../../store/user/user.thunks';

// Models
import { IUserData } from '../../../../interfaces/user.interfaces';

type TFormValues = {
  first_name: string;
  last_name: string;
  email?: string;
}

type TGeneralDataProps = {
  user: IUserData;
};

export const GeneralData: FC<TGeneralDataProps> = (
  { user }: TGeneralDataProps
) => {
  const [form] = Form.useForm<TFormValues>();

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const [formattedPhone, setFormattedPhone] = useState(user.phone);
  const [isPhoneValueValid, setIsPhoneValueValid] = useState<boolean | null>(true);

  const dispatch = useDispatch<any>();

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      form.resetFields();
      setHasChanges(false);
      toggleEditMode();
      toggleDiscardModal();
      setFormattedPhone(user.phone);
      setIsPhoneValueValid(true);
      return;
    }
    toggleDiscardModal();
  }

  const onCancelChangesHandler = (): void => {
    if (hasChanges) {
      toggleDiscardModal();
      return;
    }
    form.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name
    });
    setFormattedPhone(user.phone);
    setIsPhoneValueValid(true);
    toggleEditMode();
  }

  const onValuesChange = (_: { [key in keyof TFormValues]: string }, { first_name, last_name }: TFormValues) => {
    const isHasFirstNameChanges = user?.first_name !== first_name;
    const isHasLastNameChanges = user?.last_name !== last_name;
    const isHasPhoneChanges = user?.phone !== formattedPhone;
    const isHasChanges = isHasFirstNameChanges || isHasLastNameChanges || isHasPhoneChanges;

    setHasChanges(isHasChanges &&
      !!isPhoneValueValid &&
      first_name?.length <= 30 &&
      first_name?.length >= 1 &&
      last_name?.length <= 30 &&
      last_name?.length >= 1
    );
  };

  const toggleEditMode = (): void => {
    setEditMode(!editMode);
  }

  const saveChanges = async (): Promise<void> => {
    const profileValues = form.getFieldsValue();
    setSavingChanges(true);
    const { payload } = await dispatch(updateProfile({
      ...profileValues,
      phone: formattedPhone ?? user.phone
    }));
    setSavingChanges(false);
    if (!payload) return;
    setEditMode(false);
    setHasChanges(false);
    form.resetFields();
    setFormattedPhone(user.phone);
  }

  useEffect(() => {
    const { first_name, last_name } = form.getFieldsValue();
    const isHasFirstNameChanges = typeof first_name !== 'undefined' ? user?.first_name !== first_name : false;
    const isHasLastNameChanges = typeof last_name !== 'undefined' ? user?.last_name !== last_name : false;
    const isHasPhoneChanges = user?.phone !== formattedPhone;
    const isHasChanges = isHasFirstNameChanges || isHasLastNameChanges || isHasPhoneChanges;

    setHasChanges(isHasChanges &&
      !!isPhoneValueValid &&
      first_name?.length <= 30 &&
      first_name?.length >= 1 &&
      last_name?.length <= 30 &&
      last_name?.length >= 1
    );
  }, [formattedPhone]);

  return (
    <div className="general-data">
      <DiscardModal
        open={ discardOpen }
        onCancel={ onDiscardChangesHandler }
        title={ 'Are you sure you want to discard changes?' }
      />

      <div className="general-data--label user-data--label">
        <div className="label-title">
          General Data
        </div>
        <div className="label-actions">
          { !editMode &&
            <CustomButton type={ 'text' }
                          text={ 'Edit' }
                          size={ 'small' }
                          onClick={ toggleEditMode }/>
          }

          { editMode && <>
            { hasChanges && <CustomButton type={ 'primary' }
                                          text={ 'Save' }
                                          size={ 'small' }
                                          loading={ savingChanges }
                                          onClick={ saveChanges }/> }
            <CustomButton type={ 'text' }
                          text={ 'Cancel' }
                          size={ 'small' }
                          disabled={ savingChanges }
                          onClick={ onCancelChangesHandler }/>
          </> }
        </div>
      </div>
      <Form form={ form } className="general-data--content user-data--content" onValuesChange={ onValuesChange }>
        <div className="content-item split-items">
          <div className="content-item--field">
            { !editMode && <InputReadOnly label={ 'First Name' } textEllipsis={ true } value={ user.first_name }/> }

            { editMode && <Form.Item
              initialValue={ user.first_name }
              name="first_name"
              label="first name"
              rules={ [
                { required: true, max: 30 },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || new RegExp(stringPattern).test(getFieldValue('first_name'))) {
                      return Promise.resolve();
                    }
                    if (value.length > 30) {
                      return Promise.resolve();
                    }
                    setHasChanges(false);
                    return Promise.reject(new Error(defaultValidateMessages.invalidFirstNameFormat));
                  }
                })
              ] }>
              <CustomInput type={ 'text' } placeholder={ 'First Name*' }/>
            </Form.Item> }
          </div>

          <div className="content-item--field">
            { !editMode && <InputReadOnly label={ 'Last Name' } textEllipsis={ true } value={ user.last_name }/> }

            { editMode && <Form.Item
              initialValue={ user.last_name }
              name="last_name"
              label="last name"
              rules={ [
                { required: true, max: 30 },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || new RegExp(stringPattern).test(getFieldValue('last_name'))) {
                      return Promise.resolve();
                    }
                    if (value.length > 30) {
                      return Promise.resolve();
                    }
                    setHasChanges(false);
                    return Promise.reject(new Error(defaultValidateMessages.invalidLastNameFormat));
                  }
                })
              ] }>
              <CustomInput type={ 'text' } placeholder={ 'Last name*' }/>
            </Form.Item> }
          </div>
        </div>

        <div className="content-item split-items">

          <div className="content-item--field">
            { !editMode && <InputReadOnly label={ 'Email' } textEllipsis={ true } value={ user.email }/> }

            { editMode && <Form.Item
              initialValue={ user.email }
              name="email"
              label="email">
              <CustomInput type={ 'text' } shouldTrim={true} placeholder={ 'Email*' } disabled={ true }/>
            </Form.Item> }
          </div>

          <div className="content-item--field">
            { !editMode && <InputReadOnly label={ 'Mobile Phone' } textEllipsis={ true } value={ user?.phone }
                                          icon={ user?.phone ? <CustomIcon name={ EIconName.Smartphone }/> : '' }/> }

            { editMode &&
              <CustomPhoneNumber
                PhoneInput={ PhoneInput }
                phone={ user.phone ?? '' }
                isPhoneNumberValid={ (value) => {
                  // checkFormValid(value);
                  setIsPhoneValueValid(value);
                } }
                onChangeFormattedPhone={ (value) => {
                  setFormattedPhone(value);
                } }
              /> }
          </div>
        </div>
      </Form>
    </div>
  );
}
